/**
 * Search and Sort Models
 * Provides type-safe interfaces for search and sorting functionality
 */

/**
 * Sort direction enum
 */
export enum SortDirection {
    ASC = 'asc',
    DESC = 'desc',
    NONE = 'none'
}

/**
 * Data type for sorting
 */
export enum SortType {
    STRING = 'string',
    NUMBER = 'number',
    DATE = 'date',
    BOOLEAN = 'boolean'
}

/**
 * Sort configuration for a column
 */
export interface ISortConfig {
    /**
     * Field name to sort by
     */
    field: string;

    /**
     * Sort direction
     */
    direction: SortDirection;

    /**
     * Data type of the field
     */
    type: SortType;

    /**
     * Optional custom comparator function
     */
    comparator?: (a: any, b: any) => number;
}

/**
 * Search configuration for data filtering
 */
export interface IDataSearchConfig {
    /**
     * Search term
     */
    searchTerm: string;

    /**
     * Fields to search in
     */
    searchFields: string[];

    /**
     * Case sensitive search
     */
    caseSensitive?: boolean;

    /**
     * Exact match vs partial match
     */
    exactMatch?: boolean;

    /**
     * Custom search predicate
     */
    customPredicate?: (item: any, searchTerm: string) => boolean;
}

/**
 * Filter configuration for data filtering
 */
export interface IDataFilterConfig {
    /**
     * Field to filter
     */
    field: string;

    /**
     * Filter value(s)
     */
    value: any | any[];

    /**
     * Filter operator
     */
    operator: FilterOperator;

    /**
     * Data type
     */
    type: SortType;
}

/**
 * Filter operators
 */
export enum FilterOperator {
    EQUALS = 'equals',
    NOT_EQUALS = 'notEquals',
    CONTAINS = 'contains',
    NOT_CONTAINS = 'notContains',
    STARTS_WITH = 'startsWith',
    ENDS_WITH = 'endsWith',
    GREATER_THAN = 'greaterThan',
    GREATER_THAN_OR_EQUAL = 'greaterThanOrEqual',
    LESS_THAN = 'lessThan',
    LESS_THAN_OR_EQUAL = 'lessThanOrEqual',
    BETWEEN = 'between',
    IN = 'in',
    NOT_IN = 'notIn',
    IS_NULL = 'isNull',
    IS_NOT_NULL = 'isNotNull'
}

/**
 * Pagination configuration
 */
export interface IPaginationConfig {
    /**
     * Current page (0-based)
     */
    currentPage: number;

    /**
     * Items per page
     */
    pageSize: number;

    /**
     * Total items
     */
    totalItems: number;

    /**
     * Total pages
     */
    totalPages?: number;
}

/**
 * Complete table state including search, sort, filter, and pagination
 */
export interface ITableState<T = any> {
    /**
     * Original data
     */
    data: T[];

    /**
     * Filtered and sorted data
     */
    displayData: T[];

    /**
     * Search configuration
     */
    search?: ISearchConfig;

    /**
     * Sort configurations (supports multi-column sort)
     */
    sort?: ISortConfig[];

    /**
     * Filter configurations
     */
    filters?: IFilterConfig[];

    /**
     * Pagination configuration
     */
    pagination?: IPaginationConfig;

    /**
     * Loading state
     */
    loading?: boolean;
}

/**
 * Search result with highlighting
 */
export interface ISearchResult<T = any> {
    /**
     * Matched item
     */
    item: T;

    /**
     * Match score (0-1)
     */
    score?: number;

    /**
     * Matched fields
     */
    matchedFields?: string[];

    /**
     * Highlighted text
     */
    highlights?: { [field: string]: string };
}

/**
 * Date range for filtering
 */
export interface IDateRange {
    /**
     * Start date
     */
    start: Date | string;

    /**
     * End date
     */
    end: Date | string;
}

/**
 * Number range for filtering
 */
export interface INumberRange {
    /**
     * Minimum value
     */
    min: number;

    /**
     * Maximum value
     */
    max: number;
}

/**
 * Column definition for tables
 */
export interface IColumnDefinition {
    /**
     * Column field name
     */
    field: string;

    /**
     * Display header
     */
    header: string;

    /**
     * Data type
     */
    type: SortType;

    /**
     * Is sortable
     */
    sortable?: boolean;

    /**
     * Is searchable
     */
    searchable?: boolean;

    /**
     * Is filterable
     */
    filterable?: boolean;

    /**
     * Default sort direction
     */
    defaultSortDirection?: SortDirection;

    /**
     * Custom sort function
     */
    sortComparator?: (a: any, b: any) => number;

    /**
     * Format function for display
     */
    formatter?: (value: any) => string;

    /**
     * Filter options (for dropdown filters)
     */
    filterOptions?: { label: string; value: any }[];
}

// ========================================
// Backward Compatibility Aliases
// ========================================
/**
 * @deprecated Use IDataSearchConfig instead
 * Alias for backward compatibility
 */
export type ISearchConfig = IDataSearchConfig;

/**
 * @deprecated Use IDataFilterConfig instead
 * Alias for backward compatibility  
 */
export type IFilterConfig = IDataFilterConfig;
